// Pure helpers for the V3 activity views (M11–M13, M58–M68, M104–M109, M118); no network, no storage.
import { own, text, accessFeatures, ACCESS_FEATURES, spotsLeft, validIsoDate } from "./memberDerive";

const str = (v) => (typeof v === "string" ? v.trim() : "");
const fold = (v) => str(v).toLocaleLowerCase("de").normalize("NFD").replace(/[\u0300-\u036f]/g, "");
const isRec = (v) => v !== null && typeof v === "object" && !Array.isArray(v);
const KEYS = ACCESS_FEATURES.map(([k]) => k).filter((k) => k !== "assistance_dog_info");

// M59 choice per feature: "pflicht" = present required, "offen" = present or still unknown.
export const ACCESS_CHOICES = [["", "Kein Filter gewählt"], ["pflicht", "Vorhanden erforderlich"], ["offen", "Unbekannte mit anzeigen"]];
export const FILTER_FEATURES = ACCESS_FEATURES.filter(([k]) => KEYS.includes(k));

// /aktivitaeten?q=&zugang=step_free:pflicht,toilet:offen – unknown keys or values are ignored.
export function readActivityFilter(search) {
  const p = new URLSearchParams(typeof search === "string" ? search : "");
  const access = {};
  for (const part of (p.get("zugang") || "").split(",")) {
    const [k, v] = part.split(":");
    if (KEYS.includes(k) && (v === "pflicht" || v === "offen")) access[k] = v;
  }
  return { q: (p.get("q") || "").slice(0, 100), access };
}

export function activityQuery({ q, access }) {
  const p = new URLSearchParams();
  if (q && q.trim()) p.set("q", q.trim());
  const z = KEYS.filter((k) => access && (access[k] === "pflicht" || access[k] === "offen")).map((k) => `${k}:${access[k]}`).join(",");
  if (z) p.set("zugang", z);
  const s = p.toString();
  return s ? `?${s.replace(/%3A/g, ":").replace(/%2C/g, ",")}` : "";
}

export const accessChoiceCount = (f) => Object.keys(f.access || {}).length;

export function filterActivities(list, { q = "", access = {} } = {}) {
  const needle = fold(q);
  return (Array.isArray(list) ? list : []).filter((a) => {
    if (needle && ![text(own(a, "title")), own(a, "location"), own(a, "meeting_point"), text(own(a, "description"))].some((v) => fold(v).includes(needle))) return false;
    const f = accessFeatures(a);
    return Object.entries(access).every(([k, mode]) => {
      const x = f.find((y) => y.key === k);
      return x && (x.state === "present" || (mode === "offen" && x.state === "unknown"));
    });
  });
}

export const ACCESS_PILL = { present: ["mint", "Vorhanden"], absent: ["red", "Nicht vorhanden"], unknown: ["sand", "Unbekannt"] };

// Newest valid "Stand" and the sources of known features (M12/M60 footer); nothing is invented.
export function accessMeta(activity) {
  const known = accessFeatures(activity).filter((f) => f.state !== "unknown");
  const dates = known.map((f) => f.updatedAt).filter(Boolean).sort();
  const sources = [...new Set(known.map((f) => f.source).filter(Boolean))];
  return { source: sources.join(" · "), updatedAt: dates.length ? dates[dates.length - 1] : null };
}

export function seatsText(a) {
  const free = spotsLeft(a);
  if (free === null) return "Keine Begrenzung angegeben";
  return `${Number(a.participants_count)} von ${Number(a.capacity)} belegt · ${free} frei`;
}

const day = (iso) => (validIsoDate(iso) ? new Intl.DateTimeFormat("de-CH", { timeZone: "Europe/Zurich", day: "2-digit", month: "2-digit", year: "numeric" }).format(new Date(iso)) : "");
export const activityDay = (a) => {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(str(own(a, "date_local")));
  return m ? `${m[3]}.${m[2]}.${m[1]}` : day(own(a, "date")) || "Termin offen";
};

// M58: numbered entries for activities with a place; the overview is schematic, never geographic.
export function mapEntries(list) {
  return (Array.isArray(list) ? list : []).filter((a) => str(own(a, "location")) || str(own(a, "meeting_point")))
    .map((a, i) => ({ n: i + 1, id: String(a.id), title: text(a.title) || "Aktivität", place: [str(a.location), str(a.meeting_point)].filter(Boolean).join(" · "), day: activityDay(a),
      x: 18 + ((i * 37) % 64), y: 22 + ((i * 53) % 56) }));
}

// V.5 (not in Core 1.1): series of the same activity, only by an own string series_id.
export const seriesId = (a) => (typeof own(a, "series_id") === "string" && own(a, "series_id").trim() ? own(a, "series_id").trim() : "");
export const seriesOf = (list, id) => (Array.isArray(list) ? list.filter((a) => seriesId(a) === id) : []);

const TIME = /^([01]\d|2[0-3]):[0-5]\d$/;
const slot = (v) => (isRec(v) && validIsoDate(own(v, "start")) ? { start: v.start, end: validIsoDate(own(v, "end")) ? v.end : null, meetingPoint: str(own(v, "meeting_point")) } : null);

// V.4 (not in Core 1.1): change{previous, current, requires_confirmation:true}; anything else = no confirmable change.
export function changeInfo(a) {
  const c = own(a, "change");
  if (!isRec(c) || own(c, "requires_confirmation") !== true) return null;
  const previous = slot(own(c, "previous"));
  const current = slot(own(c, "current"));
  return previous && current ? { previous, current } : null;
}

const STATE = (v, allowed) => (typeof v === "string" && allowed.includes(v) ? v : null);
const count = (v, max) => (Number.isInteger(v) && v >= 0 && v <= max ? v : null);

// Core v2.6: companion_status and companions_requested (0–3) per activity; the companion request travels with POST …/join.
export const COMPANION_TEXT = { angefragt: "Angefragt", "bestätigt": "Bestätigt", warteliste: "Auf der Warteliste", abgelehnt: "Abgelehnt", storniert: "Storniert" };
export const companionState = (a) => STATE(own(a, "companion_status"), Object.keys(COMPANION_TEXT));
export const companionsRequested = (a) => count(own(a, "companions_requested"), 3);

// companion_capacity / companions_count; null when the Core sends no consistent numbers.
export function companionCapacity(a) {
  const cap = count(own(a, "companion_capacity"), 10000);
  const used = count(own(a, "companions_count"), 10000);
  if (cap === null) return null;
  return cap === 0 ? { cap, used: 0, free: 0 } : used === null || used > cap ? null : { cap, used, free: cap - used };
}
export function companionSeats(a) {
  const c = companionCapacity(a);
  if (!c) return "";
  return c.cap === 0 ? "Keine Begleitplätze angeboten" : `${c.used} von ${c.cap} belegt · ${c.free} frei`;
}
// Choices for the join request: 1–3 only when the activity offers companion seats at all.
export const companionChoices = (a) => ((companionCapacity(a)?.cap || 0) > 0 ? [1, 2, 3] : []);
export const companionLabel = (n) => (n === 1 ? "1 Begleitperson" : `${n} Begleitpersonen`);
// Own companion request, e.g. "2 Begleitpersonen · Angefragt"; "" when none was requested.
export function ownCompanionText(a) {
  const n = companionsRequested(a);
  return n ? `${companionLabel(n)} · ${COMPANION_TEXT[companionState(a)] || "Status unbekannt"}` : "";
}
// A companion request still counts only while it is angefragt / bestätigt / warteliste; storniert or abgelehnt allow a new request.
export const hasActiveCompanions = (a) => Boolean(companionsRequested(a)) && ["angefragt", "bestätigt", "warteliste"].includes(companionState(a));

// M107 (v2.10): an offer is participation "angeboten" (participationState "offered"); it reserves no seat.
export const OFFER_TITLE = "Platz angeboten";
export const OFFER_TEXT = "Für dich ist noch kein Platz reserviert. Bestätige das Angebot, solange ein Platz frei ist.";

// M13 draft; the form only checks input – publishing has no Core endpoint (C.5).
export const emptyDraft = () => ({ title: "", date: "", from: "", to: "", meeting: "", cost: "", access: {}, seats: "", companions: "" });
export function draftErrors(d, step, now = Date.now()) {
  const e = {};
  if (step === 0) {
    if (!str(d.title)) e.title = "Bitte gib einen Titel ein.";
    else if (Array.from(str(d.title)).length > 80) e.title = "Der Titel darf höchstens 80 Zeichen lang sein.";
    if (!/^\d{4}-\d{2}-\d{2}$/.test(d.date) || !validIsoDate(d.date) || !TIME.test(d.from)) e.date = "Bitte wähle Datum und Beginn.";
    else if (new Date(`${d.date}T${d.from}:00`).getTime() <= now) e.date = "Bitte wähle einen Termin in der Zukunft.";
    else if (d.to && (!TIME.test(d.to) || d.to <= d.from)) e.to = "Das Ende muss nach dem Beginn liegen.";
    if (!str(d.meeting)) e.meeting = "Bitte gib einen öffentlichen Treffpunkt an – keine Wohnadresse.";
  }
  if (step === 2) {
    for (const k of ["seats", "companions"]) if (d[k] !== "" && !(/^\d+$/.test(d[k]) && Number(d[k]) <= 500)) e[k] = "Bitte gib eine ganze Zahl von 0 bis 500 ein oder lass das Feld leer.";
  }
  return e;
}
